import { formatDateAsString } from "@SonarCX/components/molecules/CustomFieldInput/Date";
import { formatDateRelative, isUUID } from "@SonarCX/utilities";
import { getFormatted } from "@SonarCX/utilities/phoneNumber";

const getName = (val: string, data: any[]) => {
  const item = data.find((d) => d?.id === val);
  return (item?.name ?? "") as string;
};

export type FormatValueTypes = {
  val: string | string[] | null;
  type: string;
  statuses: any[];
  types: Record<string, any>;
  priorities: any[];
  categories: Record<string, any>;
  users: Record<string, LocalUser>;
  teams: APITeam[];
  customFields: LocalCustomField[];
};

export type GetDisplayTypeArgs = {
  type: string;
  customFields: LocalCustomField[];
};

export type GetFormattedFieldValueArgs = {
  field: LocalCustomField;
  val: string;
  users: FormatValueTypes["users"];
};

const getFormattedFieldValue = ({
  field,
  val,
  users,
}: GetFormattedFieldValueArgs) => {
  const type = field.field_type;

  switch (type) {
    case "boolean":
      return val === "true" ? "checked" : "unchecked";
    case "date-only":
    case "date-time":
    case "month-year":
      return formatDateAsString({ type, date: new Date(val) });
    case "multiline":
    case "text":
    case "dropdown":
      return val;
    case "user":
      return getName(val, Object.values(users));
    case "phone":
      return getFormatted(val);
    case "multiselect":
      return val.split("|").join(", ");
    case "flow":
    case "table":
      return "";
    default:
      console.warn("Unhandled field type:", type);
      return val;
  }
};

export const formatValue = ({
  val,
  type,
  statuses,
  types,
  priorities,
  categories,
  users,
  teams,
  customFields,
}: FormatValueTypes) => {
  if (!val) {
    return null;
  }

  const lowerType = type.toLocaleLowerCase();

  if (lowerType === "title") {
    return val;
  }

  if (lowerType.includes("status")) {
    return getName(val as string, statuses);
  }

  if (lowerType.includes("type")) {
    return getName(val as string, Object.values(types));
  }

  if (lowerType.includes("priority")) {
    return getName(val as string, priorities);
  }

  if (lowerType.includes("category")) {
    return getName(val as string, Object.values(categories));
  }

  if (lowerType.includes("team")) {
    return getName(val as string, teams);
  }

  if (["concerningUserId", "ownerId", "primaryAssigneeId"].includes(type)) {
    return getName(val as string, Object.values(users));
  }

  if (lowerType.includes("date")) {
    return formatDateRelative(val as string, true);
  }

  if (["assigneeIds", "watcherIds"].includes(type)) {
    return (val as string[])
      .map((id) => getName(id, Object.values(users)))
      .join(", ");
  }

  if (isUUID(type)) {
    const field = customFields.find((field) => field.id === type);
    if (field) {
      return getFormattedFieldValue({ field, val: val as string, users });
    }
  }

  console.warn("Unhandled activity type:", type);
  return null;
};

export const getDisplayType = ({ type, customFields }: GetDisplayTypeArgs) => {
  const typeMap = {
    title: "title",
    statusId: "status",
    typeId: "type",
    priorityId: "priority",
    categoryId: "category",
    teamId: "team",
    concerningUserId: "client",
    ownerId: "task owner",
    primaryAssigneeId: "task lead",
    dueDate: "due date",
    assigneeIds: "assignees",
    watcherIds: "watchers",
  };

  if (type in typeMap) {
    return typeMap[type];
  }

  if (isUUID(type)) {
    const field = customFields.find((field) => field.id === type);
    if (field) {
      return field.title;
    }
  }

  console.warn("Unknown activity type:", type);
  return null;
};
